import React from 'react'
import { createPortal } from 'react-dom'
import { Printer, ArrowLeft } from 'lucide-react'
import { Button } from '@/components/ui/Button'
import { COMPANY_PROFILE } from '@/config/companyProfile'
import { formatFreeStockDetails } from '@/services/warehouse/freeStockApi'
import type { IssueSlipDetail } from '@/services/warehouse/issueSlipsApi'

interface IssueSlipPrintViewProps {
  slip: IssueSlipDetail
  onBack: () => void
}

function formatDateTimeVi(iso: string): string {
  if (!iso) return '—'
  const d = new Date(iso)
  if (Number.isNaN(d.getTime())) return iso
  const pad = (n: number) => String(n).padStart(2, '0')
  return `${pad(d.getDate())}/${pad(d.getMonth() + 1)}/${d.getFullYear()} ${pad(d.getHours())}:${pad(d.getMinutes())}`
}

export function IssueSlipPrintView({ slip, onBack }: IssueSlipPrintViewProps): React.ReactElement {
  const totalQty = slip.lines.reduce((sum, l) => sum + (l.qty || 0), 0)

  const view = (
    <div className="min-h-screen bg-slate-100 p-4 font-sans text-slate-900 print:bg-white print:p-0">
      {/* Print styles */}
      <style>{`
        @page {
          size: A5 portrait;
          margin: 8mm;
        }
        body:has(.issue-slip-print-page) #root { display: none !important; }
        @media print {
          body {
            background: #ffffff !important;
            font-size: 8pt !important;
            color: #000000 !important;
            font-family: 'Inter', sans-serif !important;
          }
          .no-print {
            display: none !important;
          }
          .issue-slip-print-page > div:first-child { padding-bottom: 2mm !important; }
          .issue-slip-print-page > div:nth-child(2) { margin-top: 3mm !important; margin-bottom: 3mm !important; }
          .issue-slip-print-page > div:nth-child(3) { margin-bottom: 2mm !important; }
          .issue-slip-print-page > div:nth-child(4) { margin-bottom: 2mm !important; }
          .issue-slip-print-page table { font-size: 7pt; line-height: 1.1; table-layout: fixed; }
          .issue-slip-print-page th,
          .issue-slip-print-page td { padding: 1mm 1.5mm !important; }
          .issue-slip-print-page th:nth-child(1),
          .issue-slip-print-page td:nth-child(1) { width: 9%; }
          .issue-slip-print-page th:nth-child(2),
          .issue-slip-print-page td:nth-child(2) { width: 28%; }
          .issue-slip-print-page th:nth-child(3),
          .issue-slip-print-page td:nth-child(3) { width: 18%; }
          .issue-slip-print-page th:nth-child(4),
          .issue-slip-print-page td:nth-child(4) { width: 12%; }
          .issue-slip-print-page th:nth-child(5),
          .issue-slip-print-page td:nth-child(5) { width: 17%; }
          .issue-slip-print-page thead { display: table-header-group; }
          .issue-slip-print-page tr,
          .issue-slip-signatures { break-inside: avoid; page-break-inside: avoid; }
          .issue-slip-signatures { margin-top: 2mm !important; padding-top: 1mm !important; }
          .issue-slip-signatures .h-36 { height: 20mm !important; }
        }
      `}</style>

      {/* Control bar */}
      <div className="no-print mx-auto mb-4 flex max-w-4xl items-center justify-between rounded-[var(--radius-md)] border border-[var(--color-border)] bg-white p-3 shadow-xs">
        <Button variant="secondary" size="sm" onClick={onBack}>
          <ArrowLeft className="mr-1.5 h-4 w-4" />
          Quay lại
        </Button>
        <div className="flex items-center gap-2">
          <Button variant="primary" size="sm" onClick={() => window.print()}>
            <Printer className="mr-1.5 h-4 w-4" />
            In phiếu xuất kho (A5)
          </Button>
        </div>
      </div>

      {/* Printable Sheet */}
      <div className="issue-slip-print-page mx-auto max-w-4xl rounded-[var(--radius-md)] border border-slate-200 bg-white p-8 shadow-sm print:max-w-none print:rounded-none print:border-none print:p-0 print:shadow-none">
        {/* Header */}
        <div className="flex items-start justify-between border-b border-slate-900 pb-4">
          <div className="flex min-w-0 items-start gap-3">
            <img src="/logo-mocphat.jpg" alt="Mộc Phát Furniture" className="h-16 w-16 shrink-0 object-contain" />
            <div className="space-y-0.5 text-[10px] leading-tight text-slate-700">
              <h1 className="text-xs font-black uppercase tracking-tight text-slate-950">
                {COMPANY_PROFILE.legalName}
              </h1>
              <p>{COMPANY_PROFILE.address}</p>
              <p>Tel: {COMPANY_PROFILE.phone} · Fax: {COMPANY_PROFILE.fax}</p>
              <p>Email: {COMPANY_PROFILE.email} · Website: {COMPANY_PROFILE.website}</p>
            </div>
          </div>
          <div className="text-right">
            <div className="font-mono text-sm font-bold text-slate-900">
              Mã: <span className="text-base whitespace-nowrap">{slip.code}</span>
            </div>
            <div className="text-xs text-slate-600">
              Ngày giờ: {formatDateTimeVi(slip.created_at)}
            </div>
            {slip.status === 'VOID' ? (
              <span className="mt-1 inline-block rounded border border-rose-500 bg-rose-50 px-2 py-0.5 text-xs font-bold text-rose-700 uppercase">
                ĐÃ HỦY
              </span>
            ) : null}
          </div>
        </div>

        {/* Title */}
        <div className="my-6 text-center">
          <h2 className="text-xl font-black uppercase tracking-wider text-slate-950">
            PHIẾU XUẤT KHO
          </h2>
        </div>

        {/* Meta info */}
        <div className="mb-4 grid grid-cols-1 gap-2 text-xs sm:grid-cols-2">
          <div>
            <span className="font-bold text-slate-700">Mục đích xuất: </span>
            <span className="font-medium text-slate-950">{slip.purpose}</span>
          </div>
          <div>
            <span className="font-bold text-slate-700">Người lập: </span>
            <span className="font-medium text-slate-950">
              {slip.created_by_name || slip.created_by}
            </span>
          </div>
          {slip.note ? (
            <div className="sm:col-span-2">
              <span className="font-bold text-slate-700">Ghi chú: </span>
              <span className="text-slate-800 italic">{slip.note}</span>
            </div>
          ) : null}
          {slip.status === 'VOID' && slip.void_reason ? (
            <div className="sm:col-span-2 text-rose-700">
              <span className="font-bold">Lý do hủy: </span>
              <span>{slip.void_reason}</span>
            </div>
          ) : null}
        </div>

        {/* Lines table */}
        <div className="mb-8 overflow-hidden rounded border border-slate-900">
          <table className="w-full border-collapse text-left text-xs">
            <thead className="border-b border-slate-900 bg-slate-100 font-bold uppercase text-slate-950">
              <tr>
                <th className="w-12 border-r border-slate-900 px-3 py-2 text-center">STT</th>
                <th className="border-r border-slate-900 px-3 py-2">Tên hàng / Vật tư</th>
                <th className="border-r border-slate-900 px-3 py-2">Chi tiết</th>
                <th className="w-20 border-r border-slate-900 px-3 py-2 text-center">ĐVT</th>
                <th className="w-28 border-r border-slate-900 px-3 py-2 text-right">Số lượng</th>
                <th className="w-36 px-3 py-2">Ghi chú</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-300">
              {slip.lines.map((line, idx) => {
                const detailsStr = formatFreeStockDetails(line.details_snapshot)
                return (
                  <tr key={line.id}>
                    <td className="border-r border-slate-300 px-3 py-2 text-center tabular-nums">
                      {idx + 1}
                    </td>
                    <td className="border-r border-slate-300 px-3 py-2 font-medium text-slate-900">
                      {line.label_snapshot}
                    </td>
                    <td className="border-r border-slate-300 px-3 py-2 text-slate-700">
                      {detailsStr}
                    </td>
                    <td className="border-r border-slate-300 px-3 py-2 text-center text-slate-800">
                      {line.uom}
                    </td>
                    <td className="border-r border-slate-300 px-3 py-2 text-right font-bold tabular-nums text-slate-950">
                      {line.qty.toLocaleString('vi-VN')}
                    </td>
                    <td className="px-3 py-2 text-slate-600">
                      {line.note || '—'}
                    </td>
                  </tr>
                )
              })}
              {/* Total row */}
              <tr className="border-t-2 border-slate-900 bg-slate-50 font-bold text-slate-950">
                <td colSpan={4} className="border-r border-slate-900 px-3 py-2 text-right uppercase">
                  Tổng cộng:
                </td>
                <td className="border-r border-slate-900 px-3 py-2 text-right font-black tabular-nums">
                  {totalQty.toLocaleString('vi-VN')}
                </td>
                <td />
              </tr>
            </tbody>
          </table>
        </div>

        {/* 3-column Signatures */}
        <section className="issue-slip-signatures mt-8 pt-4">
          <div className="grid grid-cols-3 gap-6 text-center text-xs">
            {/* Cột 1: Người lập */}
            <div className="flex flex-col justify-between h-36">
              <div>
                <p className="font-bold uppercase tracking-wide text-slate-950">
                  Người lập
                </p>
                <p className="text-[11px] text-slate-500 italic">(Ký, ghi rõ họ tên)</p>
              </div>
              <div>
                <p className="font-bold text-slate-900">
                  {slip.created_by_name || slip.created_by}
                </p>
              </div>
            </div>

            {/* Cột 2: Người nhận */}
            <div className="flex flex-col justify-between h-36">
              <div>
                <p className="font-bold uppercase tracking-wide text-slate-950">
                  Người nhận
                </p>
                <p className="text-[11px] text-slate-500 italic">(Ký, ghi rõ họ tên)</p>
              </div>
              <div>
                <p className="text-slate-400 italic">[Chữ ký / họ tên]</p>
              </div>
            </div>

            {/* Cột 3: Thủ kho */}
            <div className="flex flex-col justify-between h-36">
              <div>
                <p className="font-bold uppercase tracking-wide text-slate-950">
                  Thủ kho
                </p>
                <p className="text-[11px] text-slate-500 italic">(Ký, ghi rõ họ tên)</p>
              </div>
              <div>
                <p className="text-slate-400 italic">[Chữ ký / họ tên]</p>
              </div>
            </div>
          </div>
        </section>
      </div>
    </div>
  )

  return typeof document === 'undefined' ? view : createPortal(view, document.body)
}
